import { Images } from '@/assets/images';
import { DocTypeEnum, GenderEnum, REGEX } from '@/constant';
import { cn, dayjs, formatDate } from '@/utils';

import { isNotNilOrEmpty } from 'ramda-adjunct';

import { FormConfig } from '../define-document-write.type';

const initializeValue = {
	frName: '',
	frNationality: '',
	frGender: '',
	frDateOfBirth: '',
	frPassport: '',
	frHomeCountryAddr: '',
	frPhoneNo: '',
	occupation: '',

	desiredPlace: '',
	desiredWorkplace: '',
	desiredWorkplaceAddress: '',
	desiredWorkplaceType: '0',
	// desiredPeriod: '',
	participationClassification: '',
	// participationDate: '',
	// participationDate1: '',
	intendedAddress: '',
	intendedAddressOther: '',
	// providerEmployer: '',

	districtCountry: '',
	participationRegistYear: '',
	participationDateApplication: '',
	recommenderName: '',
	recommenderRegistNo: '',
	recommenderPhone: '',
	recommenderRelational: '',
	recommenderAddress: '',
	participationApplicant: '',
	participationApplicantSignature: '',
};

type IValues = typeof initializeValue;

export const MARRIED_CONFIG: FormConfig<IValues, DocTypeEnum.PM1_09> = {
	initializeValue,
	mapPropToValues({
		detailData,
		applicantData,
		recommenderData,
		unitData,
		applicantAssignmentListData,
	}) {
		const { unitInfo, recommender } = detailData || {};

		const currentYear = dayjs().year().toString();

		const countyDistrict = unitData?.countyDistrict
			? unitData?.countyDistrict
			: unitInfo?.countyDistrict || '';

		const seasonalWorkHistory =
			isNotNilOrEmpty(applicantAssignmentListData) &&
				Array.isArray(applicantAssignmentListData)
				? applicantAssignmentListData
					.map((assignment) => {
						const start = formatDate(assignment?.workStartDate, {
							outputFormat: 'YYYY.MM.DD',
						});
						const end = formatDate(assignment?.workEndDate, {
							outputFormat: 'YYYY.MM.DD',
						});
						const place = countyDistrict;
						return `${start} ~ ${end}, (${place})`;
					})
					.join('\n')
				: '';

		return {
			...initializeValue,
			frName: applicantData?.fullName || '',
			frPassport: applicantData?.passportNo || '',
			participationApplicantSignature: applicantData?.fullName || '',
			frNationality: applicantData?.nationalName || '',
			frGender: applicantData?.gender || '',
			frDateOfBirth: applicantData?.birthDate || '',
			frPhoneNo: applicantData?.phone || '',
			frHomeCountryAddr: applicantData?.hometownAddr || '',
			participationRegistYear: currentYear,
			recommenderName: recommenderData?.name || '',
			recommenderRegistNo: isNotNilOrEmpty(recommenderData?.birthDate)
				? `${formatDate(recommenderData?.birthDate, {
					outputFormat: 'YYMMDD',
				})}-`
				: '',
			recommenderPhone: recommenderData?.phone || '',
			recommenderRelational:
				applicantData?.recommenderRelationshipForIssueApplicationDownload ||
				(recommenderData?.id === recommender?.[0]?.recommenderId
					? recommender?.[0]?.recommenderRelationship
					: ''),
			recommenderAddress: recommenderData?.address || '',
			desiredWorkplace: seasonalWorkHistory,

			// desiredPeriod: formatDate(dayjs(), {
			// 	outputFormat: 'YYYY',
			// }),
			desiredPlace: `${unitInfo?.cityProvince} ${unitInfo?.countyDistrict ? `(${unitInfo?.countyDistrict})` : ''}`,
			districtCountry: `${unitInfo?.cityProvince} ${unitInfo?.countyDistrict ? `(${unitInfo?.countyDistrict})` : ''}`,
			participationDateApplication: formatDate(dayjs()),
		};
	},
	title: '계절근로 참여 신청서 (결혼이민자의 가족·친척)',
	pages: [
		{
			background: {
				src: Images.formMarried,
				className: 'template',
				dataSource: Images.formMarried,
			},
			textAreas: [
				{
					name: 'frName',
					maxHeight: 40,
					maxLength: 44,
					containerClassName: 'absolute top-[202px] left-[150px] w-[206px]',
					className: 'text-[14.67px] leading-none',
				},
				{
					name: 'frNationality',
					maxHeight: 40,
					maxLength: 22,
					containerClassName: 'absolute top-[202px] left-[447px] w-[72px]',
					className: 'text-[14.67px] leading-none',
				},
				{
					name: 'frPassport',
					maxHeight: 40,
					maxLength: 20,
					containerClassName: 'absolute top-[244px] left-[359px] w-[84px]',
					className: 'text-[14.67px] leading-none',
				},
				{
					name: 'occupation',
					maxHeight: 43,
					maxLength: 20,
					containerClassName: 'absolute right-[12.5%] top-[244px] w-[173px]',
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
					className: 'text-[14.67px] leading-none',
				},
				{
					name: 'frHomeCountryAddr',
					maxHeight: 43,
					maxLength: 72,
					containerClassName: 'absolute left-[25.4%] top-[287px] w-[241px]',
				},
				{
					name: 'frPhoneNo',
					maxHeight: 43,
					maxLength: 20,
					containerClassName: 'absolute left-[522px] top-[287px] w-[173px]',
				},
				(values, index) => ({
					name: 'desiredPlace',
					maxHeight: 40,
					maxLength: 60,
					className: 'text-center text-[12px] leading-none',
					containerClassName: cn(
						'absolute left-[215px] top-[383px] w-[203px]',
						values?.desiredPlace && 'bg-white'
					),
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
				}),
				(values, index) => ({
					name: 'intendedAddressOther',
					maxHeight: 20,
					maxLength: 50,
					containerClassName: cn(
						'absolute left-[273px] top-[535px] w-[340px] items-center justify-center',
						!values?.intendedAddress && 'invisible',
						values?.intendedAddressOther && 'bg-white '
					),
					className: 'text-left text-[13px] leading-tight',
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
				}),

				(values, index) => ({
					name: 'desiredWorkplace',
					maxHeight: 80,
					maxLength: 300,
					containerClassName: cn(
						'absolute left-[215px] top-[428px] w-[479px] items-center justify-center',
						values?.desiredWorkplace && 'bg-white '
					),
					className:
						'text-center text-[13px] leading-tight focus:bg-white !h-[78px]',
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
				}),
				(values, index) => ({
					name: 'desiredWorkplaceAddress',
					maxHeight: 23,
					maxLength: 50,
					containerClassName: cn(
						'absolute left-[336px] top-[602px] w-[360px] items-center justify-center',
						!values?.desiredWorkplaceType && 'invisible',
						values?.desiredWorkplaceAddress && 'bg-white '
					),
					className: 'text-left text-[13px] leading-tight',
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
				}),
				// {
				// 	name: 'desiredPeriod',
				// 	maxHeight: 55,
				// 	maxLength: 20,
				// 	containerClassName: 'absolute left-[325px] top-[558px] w-[380px]',
				// 	className: 'text-center text-[17.33px] leading-none',
				// 	regex: REGEX.ALLOW_NUMBER_SPECIAL_CHARS,
				// },
				{
					name: 'recommenderName',
					maxHeight: 52,
					maxLength: 36,
					containerClassName: 'absolute left-[157px] top-[705px] w-[196px]',
					className: 'text-[14.67px] leading-none',
				},
				{
					name: 'recommenderRegistNo',
					maxHeight: 52,
					maxLength: 33,
					containerClassName: 'absolute left-[520px] top-[705px] w-[174px] ',
					className: 'text-[14.67px] leading-none',
				},
				{
					name: 'recommenderPhone',
					maxHeight: 36,
					maxLength: 20,
					containerClassName: 'absolute left-[157px] top-[761px] w-[197px]',
					regex: REGEX.ALLOW_NUMBER_SPECIAL_CHARS,
				},
				{
					name: 'recommenderRelational',
					maxHeight: 38,
					maxLength: 33,
					containerClassName: 'absolute left-[521px] top-[761px] w-[173px]',
					className: 'text-[14.67px] leading-none',
				},
				{
					name: 'recommenderAddress',
					maxHeight: 38,
					maxLength: 33,
					containerClassName: 'absolute left-[157px] top-[801px] w-[537px]',
					className: 'text-[14.67px] leading-none',
				},
				{
					name: 'participationRegistYear',
					maxHeight: 30,
					maxLength: 4,
					containerClassName: 'absolute left-[185px] top-[851px] w-[35px]',
					regex: REGEX.ALLOW_NUMBER,
				},
				{
					name: 'districtCountry',
					maxHeight: 40,
					maxLength: 12,
					containerClassName:
						'absolute left-[253px] top-[837px] w-[127px] items-end',
				},
				{
					name: 'participationApplicantSignature',
					maxHeight: 34,
					maxLength: 50,
					containerClassName: 'absolute w-[240px] top-[957px] left-[246px]',
					className: 'text-[14.67px] leading-none',
				},
			],
			checkboxes: [
				{
					name: 'frGender',
					options: Object.values(GenderEnum),
					classNames: [
						'absolute top-[207px] left-[605px] w-[14px] h-[14px]',
						'absolute top-[228px] left-[605px] w-[14px] h-[14px]',
					],
					iconType: 'circle',
				},
				{
					name: 'participationClassification',
					options: ['New', 'Re-entry'],
					classNames: [
						'absolute top-[387px] left-[536px] w-[14px] h-[14px]',
						'absolute top-[407px] left-[536px] w-[14px] h-[14px]',
					],
					iconType: 'circle',
				},
				{
					name: 'intendedAddress',
					options: ['Family', 'Employer'],
					classNames: [
						'absolute top-[517px] left-[229px] w-[14px] h-[14px]',
						'absolute top-[517px] left-[445px] w-[14px] h-[14px]',
					],
					iconType: 'circle',
					getResetKey(currentSelectKey) {
						return ['intendedAddressOther'];
					},
				},
				{
					name: 'desiredWorkplaceType',
					options: ['0', '1'],
					classNames: [
						'absolute top-[566px] left-[367px] w-[14px] h-[14px]',
						'absolute top-[566px] left-[509px] w-[14px] h-[14px]',
					],
					iconType: 'circle',
					getResetKey(currentSelectKey) {
						return ['desiredWorkplaceAddress'];
					},
				},
			],
			datePickers: [
				{
					name: 'frDateOfBirth',
					className:
						'[&>.ant-picker-input>input]:!text-[13px] [&>.ant-picker-input>input]:!text-center w-[100%]',
					containerClassName: 'absolute left-[25.5%] top-[23%] w-[77px]',
					style: {
						width: '100%',
						textAlign: 'center',
					},
				},
				{
					name: 'participationDateApplication',
					className:
						'[&>.ant-picker-input>input]:!text-[14px] [&>.ant-picker-input>input]:!text-center w-[100%]',
					containerClassName: 'absolute left-[87px] top-[962px]',
					style: {
						width: '100%',
						textAlign: 'center',
					},
				},
			],

			signatures: [
				{
					name: 'participationApplicant',
					className: 'absolute w-[195px] h-[55px] top-[955px] left-[490px]',
				},
			],
			highlightBoxes: [
				{
					name: 'frName',
					className: 'top-[204px] left-[80px] w-[55px]',
				},
				{
					name: 'frNationality',
					className: 'top-[204px] left-[382px] w-[42px]',
				},
				{
					name: 'frGender',
					className: 'top-[204px] left-[536px] w-[42px]',
				},
				{
					name: 'frDateOfBirth',
					className: 'top-[245px] left-[95px] w-[75px]',
				},
				{
					name: 'frPassport',
					className: 'top-[245px] left-[282px] w-[75px]',
				},
				{
					name: 'occupation',
					className: 'top-[245px] left-[461px] w-[45px]',
				},
				{
					name: 'frHomeCountryAddr',
					className: 'top-[289px] left-[92px] w-[82px]',
				},
				{
					name: 'frPhoneNo',
					className: 'top-[289px] left-[453px] w-[60px]',
				},
				{
					name: 'desiredPlace',
					className: 'top-[383px] left-[79px] w-[113px]',
				},
				{
					name: 'participationClassification',
					className: 'top-[390px] left-[428px] w-[84px]',
				},
				{
					name: 'desiredWorkplace',
					className: 'top-[422px] left-[68px] w-[135px] !h-[40px]',
				},
				{
					name: 'intendedAddress',
					className: 'top-[506px] left-[68px] w-[135px]',
				},
				(values, index) => ({
					name: 'intendedAddressOther',
					className: cn('top-[535px] left-[273px] w-[340px] !h-[20px]', !values?.intendedAddress && 'invisible'),
				}),
				{
					name: 'desiredWorkplaceType',
					className: 'top-[572px] left-[79px] w-[115px]',
				},
				(values, index) => ({
					name: 'desiredWorkplaceAddress',
					className: cn('top-[602px] left-[336px] w-[360px]', !values?.desiredWorkplaceType && 'invisible'),
				}),
				{
					name: 'recommenderName',
					className: 'top-[706px] left-[74px] w-[71px]',
				},
				(values, index) => ({
					name: 'recommenderRegistNo',
					className: 'top-[705px] left-[363px] w-[151px] !h-[41px]',
					hasError: values?.recommenderRegistNo?.length < 13,
				}),
				{
					name: 'recommenderPhone',
					className: 'top-[761px] left-[74px] w-[72px] ',
				},
				{
					name: 'recommenderRelational',
					className: 'top-[761px] left-[363px] w-[145px] !h-[24px]',
				},
				{
					name: 'recommenderAddress',
					className: 'top-[801px] left-[74px] w-[72px] ',
				},
				{
					name: 'participationRegistYear',
					className: 'top-[854px] left-[184px] w-[34px]',
				},
				{
					name: 'districtCountry',
					className: 'top-[854px] left-[253px] w-[127px]',
				},
				{
					name: 'participationDateApplication',
					className: 'top-[924px] left-[114px] w-[81px] !h-[16px]',
				},
				{
					name: 'participationApplicantSignature',
					className: 'top-[924px] left-[337px] w-[65px] !h-[16px]',
				},
				{
					name: 'participationApplicant',
					className: 'top-[924px] left-[535px] w-[117px] !h-[16px]',
				},
			],
		},
	],
};
